// BankLogo Component - Reusable component for displaying bank logos
import React from 'react';
import {
  getBankLogoSvgPath,
  hasLocalBankLogo,
  getBankFallbackColor,
  getBankInitials,
  formatBankDisplayName,
  getAllLocalBankNames
} from '../../utils/bankLogoUtils';

export interface BankLogoProps {
  bankName: string;
  size?: 'small' | 'medium' | 'large';
  className?: string;
  showFallback?: boolean;
  altText?: string;
  onLoad?: () => void;
  onError?: () => void;
  variant?: 'circle' | 'square';
  border?: boolean;
}

export interface BankLogoResult {
  url: string;
  isDefault: boolean;
  bankName: string;
  loading?: boolean;
}

const sizeMap = {
  small: 24,
  medium: 32,
  large: 48
};

/**
 * Simplified BankLogo Component - Uses only synchronous operations
 */
const BankLogo: React.FC<BankLogoProps> = ({
  bankName,
  size = 'medium',
  className = '',
  showFallback = true,
  altText,
  onLoad,
  onError,
  variant = 'circle',
  border = false
}) => {
  const [imageError, setImageError] = React.useState(false);
  const pixelSize = sizeMap[size];
  const displayName = formatBankDisplayName(bankName);
  const finalAltText = altText || `${displayName} logo`;

  // Synchronous logo resolution
  const hasLocalLogo = hasLocalBankLogo(bankName);
  const logoPath = getBankLogoSvgPath(bankName);
  const shouldShowFallback = !hasLocalLogo || !logoPath;

  
  const style: React.CSSProperties = {
    width: `${pixelSize}px`,
    height: `${pixelSize}px`,
    borderRadius: variant === 'circle' ? '50%' : '8px',
    border: border ? '1px solid #e5e7eb' : 'none',
    objectFit: 'contain',
    backgroundColor: shouldShowFallback ? 'transparent' : '#f9fafb'
  };

  const containerClasses = [
    'bank-logo',
    `bank-logo--${size}`,
    `bank-logo--${variant}`,
    shouldShowFallback ? 'bank-logo--fallback' : '',
    border ? 'bank-logo--bordered' : '',
    className
  ].filter(Boolean).join(' ');

  // Render local SVG logo if available and not failed
  if (hasLocalLogo && logoPath && !imageError) {
    return (
      <img
        src={logoPath}
        alt={finalAltText}
        className={containerClasses}
        style={style}
        onLoad={() => {
          console.log('BankLogo loaded successfully:', logoPath);
          onLoad?.();
        }}
        onError={(e) => {
          console.error('BankLogo failed to load:', {
            logoPath,
            error: e,
            bankName
          });
          setImageError(true);
          onError?.();
        }}
        title={finalAltText}
      />
    );
  }

  // Render fallback if enabled
  if (showFallback) {
    return <BankFallbackIcon bankName={bankName} size={pixelSize} variant={variant} border={border} className={containerClasses} />;
  }

  // No fallback, return null
  return null;
};

/**
 * BankFallbackIcon Component - Simplified fallback with initials
 */
interface BankFallbackIconProps {
  bankName: string;
  size: number;
  variant?: 'circle' | 'square';
  border?: boolean;
  className?: string;
}

const BankFallbackIcon: React.FC<BankFallbackIconProps> = ({
  bankName,
  size,
  variant = 'circle',
  border = false,
  className = ''
}) => {
  const color = getBankFallbackColor(bankName);
  const initials = getBankInitials(bankName);

  const svg = `
    <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
      <circle cx="${size/2}" cy="${size/2}" r="${(size/2) - 1}" fill="${color}"/>
      <text x="${size/2}" y="${size/2}"
            font-family="Montserrat, Arial, sans-serif"
            font-size="${size/3}"
            font-weight="600"
            text-anchor="middle"
            dominant-baseline="middle"
            fill="white">
        ${initials}
      </text>
    </svg>
  `;

  const style: React.CSSProperties = {
    width: `${size}px`,
    height: `${size}px`,
    borderRadius: variant === 'circle' ? '50%' : '8px',
    border: border ? '1px solid #e5e7eb' : 'none',
  };

  return (
    <div
      className={className}
      style={style}
      title={bankName}
      role="img"
      aria-label={`${bankName} logo`}
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
};

export default BankLogo;

// BankLogoWithLoading - Simplified version (no actual loading needed)
export const BankLogoWithLoading: React.FC<BankLogoProps & {
  showLoadingSkeleton?: boolean;
}> = ({
  showLoadingSkeleton = false,
  ...props
}) => {
  // With the new synchronous API, no loading is needed
  if (showLoadingSkeleton) {
    return <BankLogo {...props} />;
  }

  return <BankLogo {...props} />;
};

// BankLogoGroup - Component for displaying multiple bank logos
export interface BankLogoGroupProps {
  banks: string[];
  size?: 'small' | 'medium' | 'large';
  maxVisible?: number;
  showMoreIndicator?: boolean;
  className?: string;
  variant?: 'circle' | 'square';
}

export const BankLogoGroup: React.FC<BankLogoGroupProps> = ({
  banks,
  size = 'small',
  maxVisible = 5,
  showMoreIndicator = true,
  className = '',
  variant = 'circle'
}) => {
  const visibleBanks = banks.slice(0, maxVisible);
  const remainingCount = Math.max(0, banks.length - maxVisible);

  return (
    <div className={`bank-logo-group ${className}`}>
      <div className="bank-logo-group__logos">
        {visibleBanks.map((bank, index) => (
          <div
            key={`${bank}-${index}`}
            className="bank-logo-group__logo-wrapper"
            style={{ marginLeft: index > 0 ? '-8px' : '0' }}
          >
            <BankLogo
              bankName={bank}
              size={size}
              variant={variant}
              border={true}
            />
          </div>
        ))}
        {showMoreIndicator && remainingCount > 0 && (
          <div
            className="bank-logo-group__more-indicator"
            style={{
              width: `${sizeMap[size]}px`,
              height: `${sizeMap[size]}px`,
              marginLeft: visibleBanks.length > 0 ? '-8px' : '0'
            }}
          >
            <span className="bank-logo-group__more-text">
              +{remainingCount}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

// BankLogoSelector - Simplified component using local bank list
export interface BankLogoSelectorProps {
  onSelect: (bankName: string) => void;
  selectedBank?: string;
  className?: string;
  placeholder?: string;
}

export const BankLogoSelector: React.FC<BankLogoSelectorProps> = ({
  onSelect,
  selectedBank,
  className = '',
  placeholder = 'Select a bank...'
}) => {
  const [searchTerm, setSearchTerm] = React.useState('');

  // Get all local banks (synchronous)
  const allBanks = React.useMemo(() => getAllLocalBankNames(), []);

  const popularBanks = [
    'Banco do Brasil',
    'Bradesco',
    'Itaú',
    'Santander',
    'Caixa',
    'Nubank',
    'Inter',
    'C6 Bank',
    'BTG Pactual',
    'PicPay',
    'Banco Original',
    'Neon'
  ];

  const filteredBanks = React.useMemo(() => {
    if (!searchTerm.trim()) {
      return popularBanks;
    }

    return allBanks.filter(bank =>
      bank.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm, allBanks]);

  return (
    <div className={`bank-logo-selector ${className}`}>
      <input
        type="text"
        placeholder={placeholder}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="bank-logo-selector__input"
      />

      <div className="bank-logo-selector__results">
        {filteredBanks.map((bank) => (
          <div
            key={bank}
            className={`bank-logo-selector__option ${
              selectedBank === bank ? 'bank-logo-selector__option--selected' : ''
            }`}
            onClick={() => onSelect(bank)}
          >
            <BankLogo bankName={bank} size="small" />
            <span className="bank-logo-selector__option-text">{bank}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Export components
export { BankLogo };